import { logger } from "$lib/utils/logger";
import { createFetchWithRetry } from "$lib/utils/fetchHelpers";
import { reportError } from "$lib/errorHandling/errorReporter";

type ISO8601String = string;

interface Configuration {
  language: string;
  timezone: string;
  timestamp: ISO8601String;
  settings: ConfigurationSettings;
  screen?: ConfigurationScreen;
}

// Valid values for enum settings - types are derived from this
const VALID_VALUES = {
  service_worker_override: ["enable", "disable", undefined],
} as const;

interface ConfigurationSettings {
  video_completed_ratio: number;
  telemetry_enabled: boolean;
  remote_logging_enabled: boolean;
  service_worker_override: (typeof VALID_VALUES.service_worker_override)[number];
  service_worker_update_interval_ms: number;
  ad_request_tracking_enabled: boolean;
  debugging_enabled: boolean;
  data_pipeline_upload_max_bytes: number;
}

interface ConfigurationScreen {
  property_logo_url?: string;
  full_bleed_logo?: boolean;
  layout?: string;
  theme?: string; // "verticalcity" | "kone" | "default"
  vision_enabled?: boolean;
}

const DEFAULT_SETTINGS: ConfigurationSettings = {
  video_completed_ratio: 0.9, // 90% of video must play to count as "played"
  telemetry_enabled: true, // TODO default to false for general availability
  remote_logging_enabled: false, // TODO default to false for general availability
  service_worker_override: undefined, // undefined means no override - rely on built-in checks
  service_worker_update_interval_ms:
    import.meta.env.MODE === "development" ? 20 * 1000 : 5 * 60 * 1000, // check every 5 minutes
  ad_request_tracking_enabled: false,
  debugging_enabled: false,
  data_pipeline_upload_max_bytes: 14_000,
};

const DEFAULT_CONFIG: Configuration = {
  language: "en",
  timezone: "America/Edmonton",
  settings: DEFAULT_SETTINGS,
  timestamp: "", // "2025-08-28T15:30:00Z"
};

// Number settings that need custom min/max bounds (defaults to 0-Infinity)
const NUMBER_BOUNDS: Partial<
  Record<keyof ConfigurationSettings, { min?: number; max?: number }>
> = {
  video_completed_ratio: { max: 1 },
  service_worker_update_interval_ms: {
    min: 60 * 1000,
    max: 24 * 60 * 60 * 1000,
  },
  data_pipeline_upload_max_bytes: { min: 1_000, max: 1_000_000 },
};

const fetch = createFetchWithRetry({
  logContext: "Elev8Api-Configuration",
});

function defaultConfiguration(): Configuration {
  return DEFAULT_CONFIG;
}

async function fetchConfiguration(displayId: string): Promise<Configuration> {
  try {
    const CONFIG_URL = `${
      import.meta.env.VITE_ELEV8_API_BASE_URL
    }/api/screens/${displayId}/configuration`;
    const response = await fetch(CONFIG_URL);
    if (!response.ok) {
      throw new Error(
        `[Configuration] Failed to get configuration. setting to default. ${response.status} ${response.statusText}`
      );
    }
    const config = await response.json();
    return validateConfig(config);
  } catch (error) {
    reportError(
      "[Configuration] Failed to fetch configuration. setting to default.",
      error
    );
  }
  return DEFAULT_CONFIG;
}

function validateConfig(config: Configuration): Configuration {
  config = validateSettings(config);
  if (isConfig(config)) {
    logger.info("[Configuration] Valid configuration fetched:", config);
    return config;
  }
  const fixedConfig = populateMissingWithDefaults(config);
  logger.warn(
    "[Configuration] Invalid or incomplete configuration. Filling in missing props with defaults",
    config,
    fixedConfig
  );
  return fixedConfig;
}

function validateSettings(config: Configuration): Configuration {
  if (!config.settings) {
    logger.warn(
      "[Configuration] No settings found. Using default settings: ",
      DEFAULT_SETTINGS
    );
    config.settings = DEFAULT_SETTINGS;
    return config;
  }
  const settings = config.settings;
  const defaultKeys = Object.keys(
    DEFAULT_SETTINGS
  ) as (keyof ConfigurationSettings)[];
  for (const key of defaultKeys) {
    validateSetting(settings, key);
  }
  return config;
}

function validateSetting<K extends keyof ConfigurationSettings>(
  settings: ConfigurationSettings,
  key: K
): void {
  const value = settings[key];
  const defaultValue = DEFAULT_SETTINGS[key];

  if (typeof defaultValue === "boolean") {
    if (typeof value === "boolean") return;
    logInvalid(key, value, defaultValue);
    settings[key] = defaultValue;
    return;
  }

  if (typeof defaultValue === "number") {
    const { min = 0, max = Infinity } = NUMBER_BOUNDS[key] ?? {};
    const num = Number(value);
    if (Number.isFinite(num) && num >= min && num <= max) {
      settings[key] = num as ConfigurationSettings[K];
      return;
    }
    logInvalid(key, value, defaultValue);
    settings[key] = defaultValue;
    return;
  }

  // Enum: must be in VALID_VALUES
  const enumValues = VALID_VALUES[
    key as keyof typeof VALID_VALUES
  ] as readonly unknown[];
  if (enumValues.includes(value)) return;
  logInvalid(key, value, defaultValue);
  settings[key] = defaultValue;
}

function logInvalid<K extends keyof ConfigurationSettings>(
  key: K,
  value: unknown,
  defaultValue: ConfigurationSettings[K]
): void {
  logger.warn(
    `[Configuration] Invalid settings.${key}: (${value}). Set to ${defaultValue}`
  );
}

// type predicate: https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
function isConfig(config: Configuration | unknown): config is Configuration {
  return (
    (config as Configuration) &&
    typeof (config as Configuration).language === "string" &&
    typeof (config as Configuration).timezone === "string" &&
    typeof (config as Configuration).settings === "object"
  );
}

function populateMissingWithDefaults(
  config: Partial<Configuration>
): Configuration {
  return {
    ...DEFAULT_CONFIG,
    ...config,
  };
}

export type { Configuration, ConfigurationScreen };
export { defaultConfiguration, fetchConfiguration };
